import { useSelector } from 'react-redux';
import { useAlert } from '@weave/alert-system';
import { CustomAxios, getResponseData } from 'redux/axios';
import {
  QueryClient,
  UseMutationResult,
  UseQueryOptions,
  useMutation,
  useQuery,
} from 'react-query';
import { selectCurrentLocationId } from 'redux/actions/location';
import {
  CustomizationFlagsRequest,
  CustomizationFlagsResponse,
} from './customization-page-v2/customization-page-v2.types';
import {
  AccountBundleQuotaBySlugRequest,
  AccountBundleQuotaBySlugResponse,
  BlockPriceByProductCodeResponse,
  Consumer,
} from '@weave/schema-gen-ts/dist/schemas/salesforce/v1/salesforce.pb';
import {
  LocationFeatureHistoryRequest,
  LocationFeatureHistoryResponse,
} from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { PackageService } from '@weave/schema-gen-ts/dist/schemas/package-service/v1/package.pb';
import { authorizedFetch } from 'helpers/utils';
import { getCustomizationsFlagsByLocationIdURL } from 'redux/actions/customization/customization.action';
import { CustomizationFlagsLocationFeaturesResponse } from 'redux/actions/customization/customization.types';
import { LocationFeatureV2 } from 'models/location-feature.model';

export const customizationFlagsQueryKeys = {
  base: ['customization-flags'] as const,
  customizationFlagsByBundles: (locationId: string) => [
    ...customizationFlagsQueryKeys.base,
    'bundles',
    locationId,
  ],
  getSFAccountBundle: (slug: string) => [
    ...customizationFlagsQueryKeys.base,
    'sf-account-bundle',
    slug,
  ],
  getQuotaList: (product: string) => [
    ...customizationFlagsQueryKeys.base,
    'quota-list',
    product,
  ],
  locationFeatures: (locationId: string) => [
    ...customizationFlagsQueryKeys.base,
    'location-features',
    locationId,
  ],
  getAuditLogs: ({ locationId, featureId }: LocationFeatureHistoryRequest) => [
    ...customizationFlagsQueryKeys.base,
    'audit-logs',
    locationId,
    featureId,
  ],
};

const LOCATION_FEATURES_STALE_TIME = 5 * 60 * 1000;
const LOCATION_FEATURES_CACHE_TIME = 10 * 60 * 1000;

const requestLocationFeatures = async (
  locationId: string
): Promise<LocationFeatureV2[]> => {
  const resp = await CustomAxios.get<CustomizationFlagsLocationFeaturesResponse>(
    getCustomizationsFlagsByLocationIdURL(locationId),
    { headers: { 'Location-Id': locationId } }
  );
  const features = resp?.data?.features ?? [];
  // Filter out any of the "Bulk Messaging *" features
  // that include the allotment number as part of the name (ex: "Bulk Messaging 1000")
  return features.filter((f) => !f.name.includes('Bulk Messaging '));
};

export const fetchLocationFeatures = (
  queryClient: QueryClient,
  locationId: string,
  { force = false }: { force?: boolean } = {}
): Promise<LocationFeatureV2[]> =>
  queryClient.fetchQuery({
    queryKey: customizationFlagsQueryKeys.locationFeatures(locationId),
    queryFn: () => requestLocationFeatures(locationId),
    staleTime: force ? 0 : LOCATION_FEATURES_STALE_TIME,
    cacheTime: LOCATION_FEATURES_CACHE_TIME,
  });

const getQueryParamsByRequest = (request: CustomizationFlagsRequest) => {
  let queryParams = '';
  if (request.products) {
    request.products.forEach((productBundle) => {
      queryParams += `products=${productBundle}&`;
    });

    queryParams += `vertical=${request.vertical}`;
  }
  return queryParams;
};

export const useFetchCustomizationsFlagByBundles = (): UseMutationResult<
  CustomizationFlagsResponse,
  any,
  CustomizationFlagsRequest,
  any
> => {
  const locationId = useSelector(selectCurrentLocationId);
  const alert = useAlert();
  return useMutation({
    mutationFn: (request) =>
      PackageService.GetActiveCustomizationFlagsBySFBundle(authorizedFetch(), request),
    mutationKey: customizationFlagsQueryKeys.customizationFlagsByBundles(locationId),
    retry: false,
    onError: () => {
      const errorMessage =
        'Something went wrong while fetching customization flag details for selected bundle';
      alert.error(errorMessage);
    },
  });
};

const getSFAccountBundle = (
  request: AccountBundleQuotaBySlugRequest
): Promise<AccountBundleQuotaBySlugResponse> => {
  return CustomAxios.get('/salesforce/v1/account/bundle/quota', {
    params: {
      slug: request.slug,
      consumer: request.consumer || Consumer.INSYS,
    },
  }).then<AccountBundleQuotaBySlugResponse>(getResponseData);
};

export const useFetchSFAccountBundle = (
  request: AccountBundleQuotaBySlugRequest,
  options?: UseQueryOptions<AccountBundleQuotaBySlugResponse>
) => {
  return useQuery({
    queryKey: customizationFlagsQueryKeys.getSFAccountBundle(request.slug ?? ''),
    queryFn: () => getSFAccountBundle(request),
    retry: false,
    ...options,
    enabled: !!request.slug && !!(options?.enabled ?? true),
  });
};

export type SFQuotaProductCode =
  | 'BULK-TEXTING'
  | 'EMAIL-MARKETING'
  | 'AI-RECEPTIONIST-VOICE'
  | 'AI-RECEPTIONIST-MESSAGING'
  | 'AI-RECEPTIONIST';

export const useGetQuotaList = <TData = BlockPriceByProductCodeResponse>(
  productCode: SFQuotaProductCode,
  options?: UseQueryOptions<BlockPriceByProductCodeResponse, unknown, TData>
) => {
  return useQuery({
    queryKey: customizationFlagsQueryKeys.getQuotaList(productCode),
    queryFn: async () =>
      await CustomAxios.get('/salesforce/v1/block-price', {
        params: {
          productCode,
          consumer: Consumer.INSYS,
        },
      }).then<BlockPriceByProductCodeResponse>(getResponseData),
    retry: false,
    ...options,
    enabled: productCode && (options?.enabled ?? true),
  });
};

export const useCustomizationFlagAuditLogs = (
  request: LocationFeatureHistoryRequest,
  options?: UseQueryOptions<
    LocationFeatureHistoryResponse,
    unknown,
    LocationFeatureHistoryResponse
  >
) => {
  return useQuery({
    queryKey: customizationFlagsQueryKeys.getAuditLogs(request),
    queryFn: async () =>
      await CustomAxios.get(
        `/platform-location-feature/v1/location/${request.locationId}/history`,
        {
          params: {
            featureId: request.featureId,
          },
        }
      ).then(getResponseData),
    retry: false,
    refetchOnMount: true,
    ...options,
    enabled: !!request.locationId && !!request.featureId && (options?.enabled ?? true),
  });
};
